<script setup lang="ts">
  import { computed } from 'vue'
  import {
    ComboboxHeader,
    ComboboxHeaderButton,
    ComboboxHeaderTitle,
    InputGroupCombobox
  } from '@gat-ship/vue-components'
  import { IconCheck } from '@gat-ship/icons'
  import { useSelectCompanyDataSource } from '../../composables/useSelectCompanyDataSource'
  import { toSelectItems } from './to-select-items'
  import type { RefDataPickerEmits, RefDataPickerProps } from './picker-props'

  /**
   * A company picker that owns its own data fetching and composes the
   * borderless `InputGroupCombobox` primitive — it brings no border of its
   * own. Drop it into a `FormField` default slot (like `InputGroupInput` /
   * `CountryPicker`) and it picks up the parent `InputGroup`'s focus / error
   * chrome.
   *
   * Usage:
   * ```vue
   * <FormField :form="form" name="companyCode" label="Company" required>
   *   <template #default="{ field, invalid }">
   *     <CompanyPicker
   *       :id="field.name"
   *       :model-value="field.state.value"
   *       :aria-invalid="invalid"
   *       @update:model-value="field.handleChange"
   *       @blur="field.handleBlur"
   *     />
   *   </template>
   * </FormField>
   * ```
   */
  defineProps<RefDataPickerProps>()
  defineEmits<RefDataPickerEmits>()

  const { companies, loadingCompanies, fetchCompanies } =
    useSelectCompanyDataSource()

  // Lazy fetch on first render
  fetchCompanies()

  const companyItems = computed(() =>
    toSelectItems(companies.value, {
      value: (c) => c.code,
      title: (c) => c.name,
      search: (c) => c.description,
      sort: (a, b) => a.name.localeCompare(b.name)
    })
  )
</script>

<template>
  <InputGroupCombobox
    :id="id"
    :items="companyItems"
    :model-value="modelValue"
    :loading="loadingCompanies"
    :placeholder="placeholder"
    :disabled="disabled"
    :aria-invalid="ariaInvalid"
    :data-testid="dataTestid"
    :input-class="inputClass"
    @update:model-value="$emit('update:modelValue', $event)"
    @blur="$emit('blur')"
  >
    <template #content-header>
      <ComboboxHeader>
        <ComboboxHeaderTitle>Companies</ComboboxHeaderTitle>
        <ComboboxHeaderButton
          v-if="canManage"
          tabindex="-1"
          @click="$emit('manage')"
        >
          Manage
        </ComboboxHeaderButton>
      </ComboboxHeader>
    </template>
    <template #item="{ item, selected }">
      <span class="flex-1 truncate text-sm">{{ item.title }}</span>
      <IconCheck
        :class="[
          'ml-auto size-3.5 shrink-0',
          selected ? 'opacity-100' : 'opacity-0'
        ]"
      />
    </template>
  </InputGroupCombobox>
</template>
